import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useActiveUnitId } from '@/contexts/ActiveUnitContext';
import type { PeriodoLetivo } from '@/hooks/usePeriodosLetivos';
import {
  classificarPeriodoSelecionado,
  type MotivoSelecaoPeriodo,
} from '@/utils/periodoLetivoAtual';
import { tentarComBackoff } from '@/utils/tentarComBackoff';

const STORAGE_PREFIX = 'edulumina:active-periodo';
const REFRESH_TIMEOUT_MS = 8000;
const REFRESH_MAX_ATTEMPTS = 4;
const REALTIME_MAX_ATTEMPTS = 5;
const REALTIME_FALLBACK_POLL_MS = 60_000;
const REALTIME_FALLBACK_GRACE_MS = 30_000;

const chaveStorage = (unidadeId: string | null) =>
  `${STORAGE_PREFIX}:${unidadeId ?? 'geral'}`;

export type OrigemPeriodoAtivo = 'auto' | 'manual';

export type RefreshErrorKind = 'timeout' | 'network' | 'permission' | 'rpc' | 'realtime' | 'unknown';

export interface RefreshError {
  kind: RefreshErrorKind;
  message: string;
  attempts: number;
  detail?: string;
}

export type RealtimeStatus = 'connecting' | 'subscribed' | 'error' | 'closed' | 'timed_out';

interface ActivePeriodoLetivoContextType {
  periodoAtivo: PeriodoLetivo | null;
  periodoResolvido: PeriodoLetivo | null;
  origem: OrigemPeriodoAtivo;
  motivo: MotivoSelecaoPeriodo | null;
  isLoading: boolean;
  isError: boolean;
  setPeriodoOverride: (periodo: PeriodoLetivo | null) => void;
  resetOverride: () => void;
  refetch: () => void;
  /** Estado do refresh manual (botão Atualizar). */
  isRefreshing: boolean;
  refreshAttempt: number;
  refreshMaxAttempts: number;
  lastRefreshError: RefreshError | null;
  lastRefreshedAt: Date | null;
  /** Atualização com retry/backoff exponencial. */
  refresh: () => Promise<void>;
  clearRefreshError: () => void;
  /** Status do canal Realtime. */
  realtimeStatus: RealtimeStatus;
  realtimeError: string | null;
  reconnectRealtime: () => void;
}

const ActivePeriodoLetivoContext = createContext<ActivePeriodoLetivoContextType | undefined>(undefined);

function classificarErro(err: any): { kind: RefreshErrorKind; detail?: string } {
  const msg = String(err?.message ?? err ?? '').toLowerCase();
  const detail = err?.message ? String(err.message) : undefined;
  if (!msg) return { kind: 'unknown', detail };
  if (err?.name === 'AbortError' || msg.includes('timeout') || msg.includes('aborted')) return { kind: 'timeout', detail };
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('network')
  ) return { kind: 'network', detail };
  if (msg.includes('permission') || msg.includes('rls') || err?.code === '42501' || err?.code === 'PGRST301') {
    return { kind: 'permission', detail };
  }
  if (err?.code || msg.includes('rpc') || msg.includes('function')) return { kind: 'rpc', detail };
  return { kind: 'unknown', detail };
}

function mensagemErro(kind: RefreshErrorKind): string {
  switch (kind) {
    case 'timeout': return 'O servidor demorou para responder. Verifique sua conexão.';
    case 'network': return 'Sem conexão com o servidor.';
    case 'permission': return 'Sem permissão para consultar o período letivo.';
    case 'rpc': return 'Erro ao executar fn_get_active_academic_term.';
    case 'realtime': return 'Atualizações em tempo real indisponíveis.';
    default: return 'Não foi possível atualizar o período letivo.';
  }
}

export const ActivePeriodoLetivoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const activeUnitId = useActiveUnitId();
  const queryClient = useQueryClient();
  const [override, setOverride] = useState<PeriodoLetivo | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAttempt, setRefreshAttempt] = useState(0);
  const [lastRefreshError, setLastRefreshError] = useState<RefreshError | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>('connecting');
  const [realtimeError, setRealtimeError] = useState<string | null>(null);
  const [reconnectNonce, setReconnectNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(chaveStorage(activeUnitId));
      setOverride(raw ? (JSON.parse(raw) as PeriodoLetivo) : null);
    } catch {
      setOverride(null);
    }
  }, [activeUnitId]);

  const query = useQuery<PeriodoLetivo | null>({
    queryKey: ['active-periodo-letivo', activeUnitId],
    queryFn: async () => {
      const { data, error } = activeUnitId
        ? await (supabase as any).rpc('fn_get_active_academic_term', { p_unidade_id: activeUnitId })
        : await (supabase as any).rpc('get_active_academic_term');
      if (error) throw error;
      const linha = Array.isArray(data) ? data[0] : data;
      return (linha ?? null) as PeriodoLetivo | null;
    },
    staleTime: 30 * 1000,
  });

  const { data: periodoResolvido, isLoading, isError, refetch } = query;

  // ==================== Realtime subscription ====================
  useEffect(() => {
    let cancelado = false;
    let currentChannel: ReturnType<typeof supabase.channel> | null = null;
    let reSubTimer: ReturnType<typeof setTimeout> | null = null;
    let tentativa = 0;

    const limparPolling = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
    };

    const iniciarFallbackPolling = () => {
      if (pollRef.current || graceRef.current) return;
      graceRef.current = setTimeout(() => {
        if (cancelado) return;
        pollRef.current = setInterval(() => {
          queryClient.invalidateQueries({ queryKey: ['active-periodo-letivo'] });
        }, REALTIME_FALLBACK_POLL_MS);
      }, REALTIME_FALLBACK_GRACE_MS);
    };

    const conectar = () => {
      if (cancelado) return;
      setRealtimeStatus('connecting');
      const ch = supabase
        .channel(`periodos_letivos:changes:${activeUnitId ?? 'geral'}:${reconnectNonce}`)
        .on(
          'postgres_changes' as any,
          { event: '*', schema: 'public', table: 'periodos_letivos' },
          (payload: any) => {
            const unidade = payload?.new?.unidade_escolar_id ?? payload?.old?.unidade_escolar_id ?? null;
            if (activeUnitId && unidade && unidade !== activeUnitId) return;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['active-periodo-letivo'] });
              queryClient.invalidateQueries({ queryKey: ['periodos_letivos'] });
              setLastRefreshedAt(new Date());
            }, 500);
          },
        )
        .subscribe((status: string, err?: any) => {
          if (cancelado) return;
          if (status === 'SUBSCRIBED') {
            const eraDegradado = tentativa > 0;
            tentativa = 0;
            setRealtimeStatus('subscribed');
            setRealtimeError(null);
            limparPolling();
            if (eraDegradado) {
              queryClient.invalidateQueries({ queryKey: ['active-periodo-letivo'] });
            }
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            setRealtimeStatus(status === 'TIMED_OUT' ? 'timed_out' : status === 'CLOSED' ? 'closed' : 'error');
            if (err) setRealtimeError(String(err?.message ?? err));
            iniciarFallbackPolling();

            if (tentativa < REALTIME_MAX_ATTEMPTS) {
              const delay = Math.min(400 * Math.pow(2, tentativa), 15_000) + Math.random() * 250;
              tentativa++;
              reSubTimer = setTimeout(() => {
                if (cancelado) return;
                try { supabase.removeChannel(ch); } catch { /* noop */ }
                conectar();
              }, delay);
            } else {
              setRealtimeError('Falha persistente na conexão em tempo real.');
            }
          }
        });
      currentChannel = ch;
    };

    conectar();

    return () => {
      cancelado = true;
      if (reSubTimer) clearTimeout(reSubTimer);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      limparPolling();
      if (currentChannel) {
        try { supabase.removeChannel(currentChannel); } catch { /* noop */ }
      }
    };
  }, [activeUnitId, queryClient, reconnectNonce]);

  const reconnectRealtime = useCallback(() => {
    setRealtimeError(null);
    setReconnectNonce((n) => n + 1);
  }, []);

  // ==================== Overrides ====================
  const setPeriodoOverride = useCallback(
    (periodo: PeriodoLetivo | null) => {
      setOverride(periodo);
      try {
        if (periodo) localStorage.setItem(chaveStorage(activeUnitId), JSON.stringify(periodo));
        else localStorage.removeItem(chaveStorage(activeUnitId));
      } catch { /* ignore */ }
    },
    [activeUnitId],
  );

  const resetOverride = useCallback(() => setPeriodoOverride(null), [setPeriodoOverride]);

  // ==================== Refresh com backoff exponencial ====================
  const refresh = useCallback(async () => {
    // Cancela ciclo anterior
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setIsRefreshing(true);
    setLastRefreshError(null);
    setRefreshAttempt(1);

    try {
      await tentarComBackoff(
        async (signal) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['active-periodo-letivo'] }),
            queryClient.invalidateQueries({ queryKey: ['periodos_letivos'] }),
          ]);

          // Timeout dedicado por tentativa
          const timeoutCtrl = new AbortController();
          const timeoutId = setTimeout(() => timeoutCtrl.abort(), REFRESH_TIMEOUT_MS);
          const onExternalAbort = () => timeoutCtrl.abort();
          signal?.addEventListener('abort', onExternalAbort, { once: true });

          try {
            const p = refetch({ throwOnError: true });
            const res: any = await Promise.race([
              p,
              new Promise((_, reject) => {
                timeoutCtrl.signal.addEventListener('abort', () => {
                  const e: any = new Error('timeout');
                  e.name = 'AbortError';
                  reject(e);
                }, { once: true });
              }),
            ]);
            if (res?.error) throw res.error;
            return res;
          } finally {
            clearTimeout(timeoutId);
            signal?.removeEventListener('abort', onExternalAbort);
          }
        },
        {
          tentativas: REFRESH_MAX_ATTEMPTS,
          delayBaseMs: 400,
          fator: 2,
          jitterMaxMs: 250,
          signal: ctrl.signal,
          onRetry: ({ tentativa }) => setRefreshAttempt(tentativa + 1),
        },
      );

      setLastRefreshedAt(new Date());
      setLastRefreshError(null);
    } catch (err: any) {
      // Cancelamento externo (novo clique) — silencia
      if (err?.name === 'AbortError' && ctrl.signal.aborted && abortRef.current !== ctrl) {
        return;
      }
      const { kind, detail } = classificarErro(err);
      setLastRefreshError({
        kind,
        message: mensagemErro(kind),
        attempts: refreshAttempt,
        detail,
      });
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
      setIsRefreshing(false);
    }
  }, [queryClient, refetch, refreshAttempt]);

  const clearRefreshError = useCallback(() => setLastRefreshError(null), []);

  useEffect(() => () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
  }, []);

  const value = useMemo<ActivePeriodoLetivoContextType>(() => {
    const periodoEfetivo = override ?? periodoResolvido ?? null;
    return {
      periodoAtivo: periodoEfetivo,
      periodoResolvido: periodoResolvido ?? null,
      origem: override ? 'manual' : 'auto',
      motivo: classificarPeriodoSelecionado(periodoEfetivo),
      isLoading,
      isError,
      setPeriodoOverride,
      resetOverride,
      refetch: () => { void refetch(); },
      isRefreshing,
      refreshAttempt,
      refreshMaxAttempts: REFRESH_MAX_ATTEMPTS,
      lastRefreshError,
      lastRefreshedAt,
      refresh,
      clearRefreshError,
      realtimeStatus,
      realtimeError,
      reconnectRealtime,
    };
  }, [override, periodoResolvido, isLoading, isError, setPeriodoOverride, resetOverride, refetch, isRefreshing, refreshAttempt, lastRefreshError, lastRefreshedAt, refresh, clearRefreshError, realtimeStatus, realtimeError, reconnectRealtime]);

  return (
    <ActivePeriodoLetivoContext.Provider value={value}>
      {children}
    </ActivePeriodoLetivoContext.Provider>
  );
};

export const useActivePeriodoLetivo = (): ActivePeriodoLetivoContextType => {
  const ctx = useContext(ActivePeriodoLetivoContext);
  if (!ctx) {
    return {
      periodoAtivo: null,
      periodoResolvido: null,
      origem: 'auto',
      motivo: null,
      isLoading: false,
      isError: false,
      setPeriodoOverride: () => {},
      resetOverride: () => {},
      refetch: () => {},
      isRefreshing: false,
      refreshAttempt: 0,
      refreshMaxAttempts: REFRESH_MAX_ATTEMPTS,
      lastRefreshError: null,
      lastRefreshedAt: null,
      refresh: async () => {},
      clearRefreshError: () => {},
      realtimeStatus: 'connecting',
      realtimeError: null,
      reconnectRealtime: () => {},
    };
  }
  return ctx;
};
